import { Fragment, cloneElement, createElement, useSyncExternalStore } from "react";
import type { ReactElement, ReactNode } from "react";

/**
 * Runtime translations for text that is not a field of the scenario object:
 * the shared player chrome (src/ui, src/devices, App) and a course's reading
 * pane. Both overlays come from the backend (`/sim/locales/<id>/<locale>.json`)
 * and are flat `{ key: text }` maps:
 *   - `tx:<english>`  a hardcoded English string, looked up by tx()
 *   - `ui:<key>`      a uiStrings key, looked up by t()/tf()
 *   - `reading:title` / `reading:kicker` the start-card title and kicker
 *   - path keys (`beats[id=...]...`) applied to the scenario by applyLocaleOverlay
 * The course overlay wins over the chrome overlay. English never loads one.
 */

type Dict = Record<string, string>;

let chrome: Dict = {};
let course: Dict = {};
let contentLocale = "en";
let version = 0;
const listeners = new Set<() => void>();

function bump(): void {
  version++;
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* a listener must never break the others */
    }
  });
}

function clean(d: Record<string, unknown> | null | undefined): Dict {
  const out: Dict = {};
  if (!d) return out;
  for (const [k, v] of Object.entries(d)) if (typeof v === "string" && v.trim()) out[k] = v;
  return out;
}

export function setChromeOverlay(overlay: Record<string, unknown> | null): void {
  chrome = clean(overlay);
  bump();
}

export function setCourseOverlay(overlay: Record<string, unknown> | null): void {
  course = clean(overlay);
  bump();
}

/** The language the course content is shown in (the overlay's locale, else "en"). */
export function setContentLocale(locale: string): void {
  contentLocale = locale || "en";
  bump();
}

export function getContentLocale(): string {
  return contentLocale;
}

/** True when the course text is translated, so a filmed English voice must not play. */
export function speakInsteadOfClip(): boolean {
  return contentLocale !== "en";
}

/** Raw lookup (course first, then chrome). */
export function overlayEntry(key: string): string | undefined {
  return course[key] ?? chrome[key];
}

function fill(s: string, vars?: Record<string, unknown>): string {
  if (!vars) return s;
  let out = s;
  for (const [k, v] of Object.entries(vars)) out = out.split(`{${k}}`).join(String(v));
  return out;
}

/** A hardcoded English string in the active language (key `tx:<english>`), else English.
 *  `{name}` placeholders are filled from `vars` like a template literal would. */
export function tx(english: string, vars?: Record<string, unknown>): string {
  if (typeof english !== "string") return english;
  const hit = english ? overlayEntry(`tx:${english}`) : undefined;
  return fill(hit ?? english, vars);
}

type RichNode = string | { v: string } | { tag: number; kids: RichNode[] };

const richCache = new Map<string, RichNode[] | null>();

/** `{name}` values, `<0>...</0>` and `<0/>` tags; null when tags do not balance. */
function parseRich(s: string): RichNode[] | null {
  const hit = richCache.get(s);
  if (hit !== undefined) return hit;
  const root: RichNode[] = [];
  const stack: { tag: number; kids: RichNode[] }[] = [];
  const top = () => (stack.length ? stack[stack.length - 1].kids : root);
  const re = /\{(\w+)\}|<(\d+)\/>|<(\d+)>|<\/(\d+)>/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let ok = true;
  while ((m = re.exec(s))) {
    if (m.index > last) top().push(s.slice(last, m.index));
    last = re.lastIndex;
    if (m[1] !== undefined) top().push({ v: m[1] });
    else if (m[2] !== undefined) top().push({ tag: Number(m[2]), kids: [] });
    else if (m[3] !== undefined) {
      const node = { tag: Number(m[3]), kids: [] as RichNode[] };
      top().push(node);
      stack.push(node);
    } else {
      const open = stack.pop();
      if (!open || open.tag !== Number(m[4])) {
        ok = false;
        break;
      }
    }
  }
  if (ok && last < s.length) top().push(s.slice(last));
  const out = ok && !stack.length ? root : null;
  richCache.set(s, out);
  return out;
}

function renderRich(nodes: RichNode[], vars: Record<string, ReactNode>, tags: ReactElement[]): ReactNode[] {
  return nodes.map((n) => {
    if (typeof n === "string") return n;
    if ("v" in n) return n.v in vars ? vars[n.v] : `{${n.v}}`;
    const kids = renderRich(n.kids, vars, tags);
    const el = tags[n.tag];
    return el ? cloneElement(el, undefined, ...kids) : createElement(Fragment, null, ...kids);
  });
}

/**
 * One translatable sentence with values and inline markup (key `tx:<template>`).
 * `{name}` takes `vars[name]`; `<0>…</0>` wraps its text in `tags[0]` (an
 * element whose own children are replaced), `<0/>` places `tags[0]` as is.
 * A translation keeps the same placeholders and tags in any order; a
 * translation whose tags do not balance falls back to English.
 */
export function txr(english: string, vars: Record<string, ReactNode> = {}, tags: ReactElement[] = []): ReactElement {
  const hit = english ? overlayEntry(`tx:${english}`) : undefined;
  const nodes = (hit && parseRich(hit)) || parseRich(english) || [english];
  return createElement(Fragment, null, ...renderRich(nodes, vars, tags));
}

/** tx() for a value that may not be a string (reading art renders data arrays). */
export function txv<T>(value: T): T {
  return (typeof value === "string" ? tx(value) : value) as T;
}

/** A course-overlay key such as `reading:title`, else the given English. */
export function courseText(key: string, english: string): string {
  return course[key] ?? tx(english);
}

export function subscribeTx(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function getTxVersion(): number {
  return version;
}

/** Re-render on every overlay change. */
export function useTxVersion(): number {
  return useSyncExternalStore(subscribeTx, getTxVersion, getTxVersion);
}
